import { useMemo, useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Alert, Button, Chip, IconButton, Stack } from '@mui/material';
import { Close, CardMembership } from '@mui/icons-material';
import PartnerOpsBar from '../../components/partners/PartnerOpsBar';
import PartnerListTable from '../../components/partners/PartnerListTable';
import PageShell from '../../components/layout/PageShell';
import PartnerUserCreateDrawer from '../../components/partners/PartnerUserCreateDrawer';
import PartnerDocumentsPanel from '../../components/partners/PartnerDocumentsPanel';
import PartnerEditDrawer from '../../components/partners/PartnerEditDrawer';
import PartnerReferralDrawer from '../../components/partners/PartnerReferralDrawer';
import PartnerWalletTransactionsDrawer from '../../components/partners/PartnerWalletTransactionsDrawer';
import {
  listPartners, blockPartner, unblockPartner, deletePartner, togglePartnerActive,
  adjustPartnerWallet, updatePartner,
  type PartnerRow,
} from '../../services/partnerUserService';
import { listZones } from '../../services/zoneService';
import { PARTNER_SERVICE_CONFIG } from '../../constants/partnerServices';
import { downloadListCsv, rowsToCsv } from '../../utils/listExportCsv';
import { createPartnerWalletAdjustHandler } from '../../utils/partnerWalletCache';

const PARTNER_EXPORT_COLUMNS = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'partnerStatus', label: 'Status' },
  { key: 'zone', label: 'Zone', getValue: (row: Record<string, unknown>) => (row.zoneId as { name?: string })?.name || '' },
  { key: 'serviceHub', label: 'Service Hub' },
  { key: 'walletBalance', label: 'Wallet Balance' },
  { key: 'isActive', label: 'Active', getValue: (row: Record<string, unknown>) => (row.isActive ? 'Yes' : 'No') },
  { key: 'isVerified', label: 'Verified', getValue: (row: Record<string, unknown>) => (row.isVerified ? 'Yes' : 'No') },
];

export default function PartnerUsersPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const subscriptionPlanId = searchParams.get('subscriptionPlanId') || '';
  const subscriptionPlanName = searchParams.get('subscriptionPlanName') || '';
  const urlEntityType = searchParams.get('entityType') || '';
  const urlZoneId = searchParams.get('zoneId') || '';
  const urlPartnerId = searchParams.get('partnerId') || '';
  const urlReviewDocuments = searchParams.get('reviewDocuments') === '1';

  const [entityTab, setEntityTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [zoneFilter, setZoneFilter] = useState(urlZoneId);
  const [hubFilter, setHubFilter] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [docsPartner, setDocsPartner] = useState<PartnerRow | null>(null);
  const [referralPartner, setReferralPartner] = useState<PartnerRow | null>(null);
  const [walletPartner, setWalletPartner] = useState<PartnerRow | null>(null);
  const [editPartner, setEditPartner] = useState<PartnerRow | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [exporting, setExporting] = useState(false);

  const entityType = PARTNER_SERVICE_CONFIG[entityTab]?.key || 'driver';
  const listEntityType = subscriptionPlanId && !urlEntityType ? undefined : entityType;

  useEffect(() => {
    if (urlEntityType) {
      const idx = PARTNER_SERVICE_CONFIG.findIndex((c) => c.key === urlEntityType);
      if (idx >= 0) setEntityTab(idx);
    }
  }, [urlEntityType]);

  useEffect(() => {
    if (urlZoneId) setZoneFilter(urlZoneId);
  }, [urlZoneId]);

  const clearSubscriptionFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('subscriptionPlanId');
    next.delete('subscriptionPlanName');
    setSearchParams(next, { replace: true });
  };

  const { data: zonesData } = useQuery({
    queryKey: ['zones-list-mini'],
    queryFn: () => listZones({ limit: 100 }),
  });
  const zones = zonesData?.rows || [];

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['partners', listEntityType, statusFilter, zoneFilter, hubFilter, search, fromDate, toDate, subscriptionPlanId, page, pageSize],
    queryFn: () => listPartners({
      entityType: listEntityType,
      status: statusFilter || undefined,
      zoneId: zoneFilter || undefined,
      serviceHub: hubFilter || undefined,
      search: search || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      partnerSubscriptionPlanId: subscriptionPlanId || undefined,
      page: page + 1,
      limit: pageSize,
    }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['partners'] });

  const blockMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => blockPartner(id, reason),
    onSuccess: invalidate,
  });
  const unblockMut = useMutation({ mutationFn: unblockPartner, onSuccess: invalidate });
  const deleteMut = useMutation({ mutationFn: deletePartner, onSuccess: invalidate });
  const toggleMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => togglePartnerActive(id, isActive),
    onSuccess: invalidate,
  });
  const walletMut = useMutation({
    mutationFn: ({ id, amount, type, description }: { id: string; amount: number; type: 'credit' | 'debit'; description: string }) =>
      adjustPartnerWallet(id, amount, type, description),
    onSuccess: createPartnerWalletAdjustHandler(queryClient),
  });
  const editMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => updatePartner(id, body),
    onSuccess: () => { invalidate(); setEditPartner(null); },
  });

  const rows = useMemo(() => {
    const list = data?.data || [];
    return [...list].sort((a, b) => {
      const aTime = a.createdAt ? Date.parse(a.createdAt) : 0;
      const bTime = b.createdAt ? Date.parse(b.createdAt) : 0;
      return bTime - aTime;
    });
  }, [data?.data]);

  const totalCount = Number(data?.total ?? rows.length) || 0;

  useEffect(() => {
    if (!urlPartnerId || !urlReviewDocuments) return;
    const fromList = rows.find((r) => r._id === urlPartnerId);
    if (fromList) {
      setDocsPartner(fromList);
      return;
    }
    let cancelled = false;
    listPartners({ entityType: urlEntityType || 'merchant', limit: 300 }).then((res) => {
      if (cancelled) return;
      const partner = res.data.find((r) => r._id === urlPartnerId);
      if (partner) setDocsPartner(partner);
    });
    return () => { cancelled = true; };
  }, [urlPartnerId, urlReviewDocuments, urlEntityType, rows]);

  const clearFilters = () => {
    setSearch('');
    setFromDate('');
    setToDate('');
    setZoneFilter('');
    setHubFilter('');
    setPage(0);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await listPartners({
        entityType: listEntityType,
        status: statusFilter || undefined,
        zoneId: zoneFilter || undefined,
        serviceHub: hubFilter || undefined,
        search: search || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        partnerSubscriptionPlanId: subscriptionPlanId || undefined,
        page: 1,
        limit: 5000,
      });
      const exportRows = result.data || [];
      const label = PARTNER_SERVICE_CONFIG[entityTab]?.key || 'partners';
      const csv = rowsToCsv(exportRows as unknown as Record<string, unknown>[], PARTNER_EXPORT_COLUMNS);
      downloadListCsv(csv, label);
    } finally {
      setExporting(false);
    }
  };

  return (
    <PageShell>
      {subscriptionPlanId && (
        <Alert
          severity="info"
          icon={<CardMembership />}
          sx={{ mb: 2, borderRadius: 2 }}
          action={(
            <Stack direction="row" spacing={1} alignItems="center">
              <Button component={RouterLink} to="/partners/subscription-plans" size="small" variant="outlined">
                Manage plans
              </Button>
              <IconButton size="small" aria-label="Clear subscription filter" onClick={clearSubscriptionFilter}>
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          )}
        >
          Showing partners subscribed to{' '}
          <Chip size="small" label={subscriptionPlanName || 'selected plan'} sx={{ mx: 0.5, fontWeight: 700 }} />
          {!urlEntityType ? ' across all partner types' : ''}
          .
        </Alert>
      )}

      <PartnerOpsBar
        entityTab={entityTab}
        onEntityTabChange={(idx) => { setEntityTab(idx); setPage(0); }}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        search={search}
        onSearchChange={(q) => { setSearch(q); setPage(0); }}
        zoneFilter={zoneFilter}
        onZoneFilterChange={(z) => { setZoneFilter(z); setPage(0); }}
        hubFilter={hubFilter}
        onHubFilterChange={(h) => { setHubFilter(h); setPage(0); }}
        fromDate={fromDate}
        onFromDateChange={setFromDate}
        toDate={toDate}
        onToDateChange={setToDate}
        zones={zones}
        totalCount={totalCount}
        onAdd={() => setCreateOpen(true)}
        onClearFilters={clearFilters}
        onExport={handleExport}
        exporting={exporting}
      />

      <PartnerListTable
        rows={rows}
        loading={isLoading && rows.length === 0}
        walletLoading={walletMut.isPending}
        onEdit={setEditPartner}
        onDocuments={setDocsPartner}
        onReferrals={setReferralPartner}
        onView={(row) => navigate(`/partners/${row._id}`, { state: { from: `${location.pathname}${location.search}` } })}
        onBlock={(row) => blockMut.mutate({ id: row._id, reason: window.prompt('Reason') || 'Blocked' })}
        onUnblock={(row) => unblockMut.mutate(row._id)}
        onDelete={(row) => { if (window.confirm('Delete this partner?')) deleteMut.mutate(row._id); }}
        onToggleActive={(row, isActive) => toggleMut.mutate({ id: row._id, isActive })}
        onWalletAdjust={(row, mode, amount, note) => walletMut.mutateAsync({ id: row._id, amount, type: mode, description: note })}
        onWalletView={setWalletPartner}
        onRefresh={() => refetch()}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
      />

      {createOpen && (
        <PartnerUserCreateDrawer open={createOpen} onClose={() => setCreateOpen(false)} entityType={entityType} zones={zones} />
      )}
      <PartnerDocumentsPanel open={!!docsPartner} onClose={() => setDocsPartner(null)} partner={docsPartner} entityType={entityType} />
      <PartnerReferralDrawer open={!!referralPartner} onClose={() => setReferralPartner(null)} partner={referralPartner} />
      <PartnerWalletTransactionsDrawer open={!!walletPartner} onClose={() => setWalletPartner(null)} partner={walletPartner} />
      <PartnerEditDrawer
        open={!!editPartner}
        onClose={() => setEditPartner(null)}
        partner={editPartner}
        zones={zones}
        loading={editMut.isPending}
        onSave={(body) => editPartner && editMut.mutate({ id: editPartner._id, body })}
      />
    </PageShell>
  );
}
